import * as React from "react"
import { Bug, ChevronsUpDown, Lightbulb, LogOut, Phone } from "lucide-react"

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar"
import { avatarColor } from "@/lib/avatar-color"
import store from "../learning_strategists/state/store"
import NotificationStatus from "../learning_strategists/components/notification_status"
import VolumeSlider from "../learning_strategists/components/volume_slider"
import CallNewLeads from "../learning_strategists/components/call_new_leads"
import Modal from "../learning_strategists/components/modal"

// Shared dropdown body — also used by NavTop in topbar mode.
export function UserMenuItems({ user, ui, onConnectSwitch }) {
  const keepOpen = (e) => e.preventDefault()

  return (
    <>
      <DropdownMenuGroup>
        <DropdownMenuItem asChild>
          <a
            href="https://form.asana.com/?hash=6ea39f2be7c1a4f8e31f13df8682105f9751f686ea98a573fbaf42846bf24892&id=1122551254614407"
            target="_blank"
            rel="noreferrer"
          >
            <Bug />
            Report Bug
          </a>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <a
            href="https://form.asana.com?hash=ebb43deb7ef1bbd38d2cc4040118d5c62fdcb23e493bdf2f1be94f39c30c0596&id=1128868809335714"
            target="_blank"
            rel="noreferrer"
          >
            <Lightbulb />
            Suggest Feature
          </a>
        </DropdownMenuItem>
        {typeof Notification !== "undefined" && Notification.permission !== "granted" && (
          <DropdownMenuItem onSelect={keepOpen}>
            <NotificationStatus />
            Enable notifications
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onSelect={keepOpen}>
          <CallNewLeads sales_manager={user.sales_manager} />
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={onConnectSwitch}>
          <Phone />
          Connect Switch
        </DropdownMenuItem>
      </DropdownMenuGroup>
      <DropdownMenuSeparator />
      <DropdownMenuItem onSelect={keepOpen}>
        <VolumeSlider volume={ui.volume} soundOption={ui.soundOption} />
      </DropdownMenuItem>
      <DropdownMenuSeparator />
      <DropdownMenuItem
        onSelect={() => store.emit("ui:sign_out")}
      >
        <LogOut />
        Sign Out
      </DropdownMenuItem>
    </>
  )
}

export function SwitchAssistModal({ onClose }) {
  return (
    <Modal title="Switch Telecom Softphone" onClose={onClose}>
      <div className="space-y-6 p-6 pt-2">
        <div>
          Switch Telecom offers browser extensions for Firefox and Chrome which enable Click-To-Call functionality.
        </div>
        <div>
          After installing, you'll be able to click on the phone icon next to a phone number to initiate a call via Switch Telecom's soft phone interface. This also works for any phone number link displayed on any webpage.
        </div>
        <div>
          <a href="https://switchtel.co.za/clientzone/downloads/switchtel-ff-addon.xpi" target="_blank" rel="noreferrer">Install Firefox extension</a>
        </div>
        <div>
          <a href="https://chrome.google.com/webstore/detail/switch-telecom-softphone/dkmfhijfhfigkbepoaiiiamnfcmceiid" target="_blank" rel="noreferrer">Install Chrome extension</a>
        </div>
      </div>
    </Modal>
  )
}

function initials(name) {
  return (name || "")
    .split(/\s+/)
    .map((part) => part[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase()
}

export function UserAvatar({ user, className }) {
  const color = avatarColor(user.id ?? user.name)
  return (
    <Avatar className={className}>
      <AvatarImage src={user.profile_picture_url} alt={user.name} className="rounded-lg" />
      <AvatarFallback className={color.fill}>{initials(user.name)}</AvatarFallback>
    </Avatar>
  )
}

export function NavUser({ user, ui }) {
  const { isMobile } = useSidebar()
  const [showSwitchAssist, setShowSwitchAssist] = React.useState(false)
  if (!user) return null

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              <UserAvatar user={user} className="h-8 w-8 rounded-lg" />
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-medium">{user.name}</span>
                {user.email && <span className="truncate text-xs">{user.email}</span>}
              </div>
              <ChevronsUpDown className="ml-auto size-4" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
            side={isMobile ? "bottom" : "right"}
            align="end"
            sideOffset={4}
          >
            <DropdownMenuLabel className="p-0 font-normal">
              <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                <UserAvatar user={user} className="h-8 w-8 rounded-lg" />
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-medium">{user.name}</span>
                  {user.email && <span className="truncate text-xs">{user.email}</span>}
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <UserMenuItems user={user} ui={ui} onConnectSwitch={() => setShowSwitchAssist(true)} />
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
      {showSwitchAssist && <SwitchAssistModal onClose={() => setShowSwitchAssist(false)} />}
    </SidebarMenu>
  )
}
